/**
 * Ramp Embed - Marqeta Renderer
 */
import { logger } from "../logger";
import {
  type EmbedConfig,
  EmbedError,
  type ExchangeSessionTokenResponse,
} from "../types";

interface MountMarqetaOptions {
  container: HTMLElement;
  issuerData: ExchangeSessionTokenResponse;
  config: EmbedConfig;
  jti: string;
  onCopied: (field: "number" | "expiry" | "cvv" | "pin") => void;
}

declare global {
  interface Window {
    marqeta?: { bootstrap: (config: unknown) => void };
  }
}

const SDK_URL = "https://widgets.marqeta.com/marqetajs/2.0.0/marqeta.min.js";
const SANDBOX_URL =
  "https://widgets-sandbox.marqeta.com/marqetajs/2.0.0/marqeta.min.js";

export async function mountMarqeta({
  container,
  issuerData,
  config,
  jti,
  onCopied,
}: MountMarqetaOptions): Promise<void> {
  const startTime = performance.now();

  const { issuer_token, pin_is_set } = issuerData;

  await loadSdk(config.isProduction);

  if (!window.marqeta) {
    throw new EmbedError("CARD_DISPLAY_FAILED", "Failed to load card");
  }

  const copyIcon = `<svg class="copy-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;

  container.innerHTML = `
    <div class="card-details" id="mq-container">
      <div class="card-field">
        <div class="card-field-label">Card number</div>
        <div class="card-field-row">
          <div id="mq-pan"></div>
          <div class="copy-btn">
            <div id="mq-pan-copy" class="copy-btn-target"></div>
            ${copyIcon}
          </div>
        </div>
      </div>
      <div class="card-field-group">
        <div class="card-field">
          <div class="card-field-label">EXP</div>
          <div class="card-field-row">
            <div id="mq-exp"></div>
            <div class="copy-btn">
              <div id="mq-exp-copy" class="copy-btn-target"></div>
              ${copyIcon}
            </div>
          </div>
        </div>
        <div class="card-field">
          <div class="card-field-label">CVV</div>
          <div class="card-field-row">
            <div id="mq-cvv"></div>
            <div class="copy-btn">
              <div id="mq-cvv-copy" class="copy-btn-target"></div>
              ${copyIcon}
            </div>
          </div>
        </div>
        ${
          pin_is_set
            ? `
        <div class="card-field">
          <div class="card-field-label">PIN</div>
          <div class="card-field-row">
            <div id="mq-pin"></div>
            <div class="copy-btn">
              <div id="mq-pin-copy" class="copy-btn-target"></div>
              ${copyIcon}
            </div>
          </div>
        </div>
        `
            : ""
        }
      </div>
    </div>
  `;

  const style = {
    span: {
      color: "#1a1a1a",
      "font-family":
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      "font-size": "16px",
    },
  };

  const BOOTSTRAP_TIMEOUT_MS = 15000;

  return new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new EmbedError("CARD_DISPLAY_FAILED", "Card display timed out"));
    }, BOOTSTRAP_TIMEOUT_MS);

    window.marqeta!.bootstrap({
      clientAccessToken: issuer_token,
      integrationType: "custom",
      containerId: "mq-container",
      component: {
        showPan: {
          cardPan: { domId: "mq-pan", format: true, styles: style },
          copyCardPan: {
            domId: "mq-pan-copy",
            mode: "transparent",
            onCopySuccess: () => onCopied("number"),
          },
          cardExp: { domId: "mq-exp", format: true, styles: style },
          copyCardExp: {
            domId: "mq-exp-copy",
            mode: "transparent",
            onCopySuccess: () => onCopied("expiry"),
          },
          cardCvv: { domId: "mq-cvv", styles: style },
          copyCardCvv: {
            domId: "mq-cvv-copy",
            mode: "transparent",
            onCopySuccess: () => onCopied("cvv"),
          },
        },
      },
      callbackEvents: {
        onSuccess: () => {
          clearTimeout(timeoutId);
          const duration = Math.round(performance.now() - startTime);
          logger.info("card_mount_success", {
            jti,
            product: issuerData.issuing_product,
            duration_ms: duration,
          });
          resolve();
        },
        onFailure: () => {
          clearTimeout(timeoutId);
          const duration = Math.round(performance.now() - startTime);
          logger.error("card_mount_failed", {
            jti,
            product: issuerData.issuing_product,
            duration_ms: duration,
          });
          reject(new EmbedError("CARD_DISPLAY_FAILED", "Failed to load card"));
        },
      },
    });

    if (pin_is_set) {
      window.marqeta!.bootstrap({
        clientAccessToken: issuer_token,
        integrationType: "custom",
        options: { cardholderVerificationMethod: "OTHER" },
        component: {
          pinReveal: {
            cardPin: { domId: "mq-pin", styles: style },
            copyCardPin: {
              domId: "mq-pin-copy",
              mode: "transparent",
              onCopySuccess: () => onCopied("pin"),
            },
          },
        },
      });
    }
  });
}

let sdkPromise: Promise<void> | null = null;

function loadSdk(isProduction: boolean): Promise<void> {
  if (window.marqeta) {
    return Promise.resolve();
  }

  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = isProduction ? SDK_URL : SANDBOX_URL;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new EmbedError("CARD_DISPLAY_FAILED", "Failed to load card"));
    document.head.appendChild(script);
  });

  return sdkPromise;
}
